import type { CountdownApiClient, components } from "@countdown/api-client";
import { type ReactElement, useCallback, useState } from "react";
import { useSearchParams } from "react-router";

import { ActionChoice, defaultActionId } from "@/components/ActionChoice";
import { PageHeading } from "@/components/PageHeading";
import {
  NEXT_STEPS,
  PrizeDoorDialog,
  actorOf,
  stepText,
} from "@/components/PrizeDoorDialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  LOAD_FAILED,
  NO_LIVE_ACTION,
  type PrizeDoor,
  ROLE_MISSING,
  loadPrizeDoors,
  useResource,
} from "@/lib/api";
import { formatDate } from "@/lib/labels";
import { wordingOf } from "@/lib/wording";

/** The order of the summary line: what needs you first. */
const SUMMARY_ORDER: readonly PrizeDoor["next_step"][] = [
  "ship",
  "complete",
  "redraw",
  "sponsor_ships",
  "await_winner",
  "notify",
  "await_draw",
  "done",
  "no_participants",
];

/**
 * The prize handover (concept 9.8.2 nos. 5 and 6), one row per door whose day has come (D95).
 *
 * WHAT ONLY A PERSON CAN DO. Draw, notification, address form and substitute run as jobs. What
 * is left is shipping a prize and saying so, and drawing again where the substitutes ran out.
 * Each row names its next step and who takes it; a click opens the door's dialog with
 * everything about its draw.
 *
 * NO NAME AND NO ADDRESS IN THE TABLE. This screen sits open all day next to whoever is
 * packing. The address appears in the dialog of one door, and the server logs every read.
 *
 * ACTION AND DOOR STAND IN THE ADDRESS (`?aktion=…&tuerchen=3`), so the bell's entry for a
 * prize without a substitute opens that door's dialog, also while the page is already open.
 */
export function PrizesPage({
  actions,
}: {
  readonly actions: readonly components["schemas"]["BackofficeActionView"][];
}): ReactElement {
  // Open by default: that is the day's work. "Alle" answers "was door 3 shipped?" later.
  const [openOnly, setOpenOnly] = useState(true);
  const [params, setParams] = useSearchParams();
  // One calendar at a time (D36): the one the address names, else the running one.
  const named = params.get("aktion");
  const actionId = actions.some((action) => action.action_id === named)
    ? named
    : defaultActionId(actions);
  const namedDoor = Number(params.get("tuerchen"));
  const position = Number.isInteger(namedDoor) && namedDoor > 0 ? namedDoor : null;
  const open = (next: number | null): void => {
    setParams({
      ...(actionId === null ? {} : { aktion: actionId }),
      ...(next === null ? {} : { tuerchen: String(next) }),
    });
  };
  const load = useCallback(
    (api: CountdownApiClient) => loadPrizeDoors(api, actionId ?? ""),
    [actionId],
  );
  const doors = useResource<PrizeDoor[]>(load);
  const chosen = actions.find((action) => action.action_id === actionId);
  const wording = chosen === undefined ? undefined : wordingOf(chosen);

  if (doors.status === "forbidden") {
    return (
      <>
        <PageHeading title="Gewinne" />
        <p className="text-sm text-muted-foreground">{ROLE_MISSING}</p>
      </>
    );
  }

  const shown =
    doors.status === "ready"
      ? doors.data.filter((door) => !openOnly || NEXT_STEPS[door.next_step].open)
      : [];

  return (
    <>
      <PageHeading title="Gewinne" />

      <ActionChoice
        actions={actions}
        value={actionId}
        onChange={(id) => {
          setParams({ aktion: id });
        }}
      />

      {doors.status === "loading" ? (
        <p className="text-sm text-muted-foreground">Wird geladen …</p>
      ) : null}
      {doors.status === "error" ? (
        <p className="text-sm text-muted-foreground">{LOAD_FAILED}</p>
      ) : null}
      {doors.status === "not_found" ? (
        <p className="text-sm text-muted-foreground">{NO_LIVE_ACTION}</p>
      ) : null}

      {doors.status === "ready" ? (
        <>
          <Summary doors={doors.data} wording={wording} />

          <fieldset className="mb-4 flex gap-2">
            <legend className="sr-only">Anzeige</legend>
            {[true, false].map((value) => (
              <Button
                key={String(value)}
                type="button"
                size="sm"
                variant={openOnly === value ? "default" : "outline"}
                aria-pressed={openOnly === value}
                onClick={() => {
                  setOpenOnly(value);
                }}
              >
                {value ? "Nur offene" : "Alle"}
              </Button>
            ))}
          </fieldset>

          <Table>
            <TableCaption className="sr-only">
              {openOnly ? "Türchen mit offenem Schritt" : "Alle Türchen"}
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Türchen</TableHead>
                <TableHead>Gewinn</TableHead>
                <TableHead>Nächster Schritt</TableHead>
                <TableHead>Wer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((door) => (
                <TableRow
                  key={door.field_position}
                  className="cursor-pointer"
                  onClick={() => {
                    open(door.field_position);
                  }}
                >
                  <TableCell className="font-medium">
                    {/* The way for keyboard and screen reader; the row's click does the same. */}
                    <button
                      type="button"
                      className="underline underline-offset-4"
                      onClick={(event) => {
                        event.stopPropagation();
                        open(door.field_position);
                      }}
                    >
                      {door.field_position}
                    </button>
                    <span className="block text-xs text-muted-foreground">
                      {formatDate(door.calendar_date)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {door.prize_description ?? "Nicht beschrieben"}
                    {door.awards > 1 ? (
                      <span className="block text-xs text-muted-foreground">
                        {door.awards} Zuteilungen
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell>{stepText(door.next_step, door.due_at, wording)}</TableCell>
                  <TableCell>{actorOf(door.next_step, wording) ?? ""}</TableCell>
                </TableRow>
              ))}
              {shown.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground">
                    {openOnly ? "Nichts offen." : "Noch kein Türchen offen."}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </>
      ) : null}

      {position === null || actionId === null ? null : (
        <PrizeDoorDialog
          actionId={actionId}
          position={position}
          onClose={() => {
            open(null);
          }}
          onChanged={doors.reload}
          wording={wording}
        />
      )}
    </>
  );
}

/** Where the action stands: how many doors wait at which step. */
function Summary({
  doors,
  wording,
}: {
  readonly doors: readonly PrizeDoor[];
  readonly wording: ReturnType<typeof wordingOf> | undefined;
}): ReactElement | null {
  const counts = new Map<PrizeDoor["next_step"], number>();
  for (const door of doors) {
    counts.set(door.next_step, (counts.get(door.next_step) ?? 0) + 1);
  }
  const lines = SUMMARY_ORDER.filter((step) => counts.has(step));
  if (lines.length === 0) {
    return null;
  }
  return (
    <section aria-labelledby="gewinne-stand" className="mb-6">
      <h3 id="gewinne-stand" className="sr-only">
        Stand der Aktion
      </h3>
      <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {lines.map((step) => (
          <li key={step}>
            <span className="font-bold tabular-nums">{counts.get(step)}</span>{" "}
            {stepText(step, null, wording)}
          </li>
        ))}
      </ul>
    </section>
  );
}
